import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { STALE_TIME } from '@/api/staleTimes';
import { downloadApiFile } from '@/utils/download';
import {
  paginatedPcmpAccountsQueueSchema,
  paginatedPcmpRedisburseBatchesSchema,
  pcmpAccountsBatchFailResultSchema,
  pcmpAccountsBatchesSchema,
  pcmpAccountsBatchProposalsSchema,
  pcmpAccountsBatchReportSchema,
  pcmpAccountsDisburseResultSchema,
  pcmpAccountsQueueCountsSchema,
  pcmpRedisburseBatchProposalsSchema,
  pcmpRedisburseBatchReportSchema,
  pcmpRedisburseCreateResultSchema,
  type PaginatedPcmpAccountsQueue,
  type PaginatedPcmpRedisburseBatches,
  type PcmpAccountsBatchFailPayload,
  type PcmpAccountsBatchFailResult,
  type PcmpAccountsBatches,
  type PcmpAccountsBatchesParams,
  type PcmpAccountsBatchProposals,
  type PcmpAccountsBatchProposalsParams,
  type PcmpAccountsBatchView,
  type PcmpAccountsBatchReport,
  type PcmpAccountsDisbursePayload,
  type PcmpAccountsDisburseResult,
  type PcmpAccountsQueueCounts,
  type PcmpAccountsQueueParams,
  type PcmpRedisburseBatchesParams,
  type PcmpRedisburseBatchProposals,
  type PcmpRedisburseBatchProposalsParams,
  type PcmpRedisburseBatchReport,
  type PcmpRedisburseCreatePayload,
  type PcmpRedisburseCreateResult,
} from '../types/pcmpAccounts.types';
import type { PcmpQueueFilters } from '../types/pcmpQueue.types';

export const pcmpAccountsQueryKeys = {
  all: () => ['pcmp-accounts'] as const,
  queue: (params: PcmpAccountsQueueParams) =>
    [...pcmpAccountsQueryKeys.all(), 'queue', params] as const,
  counts: (filters: PcmpQueueFilters) =>
    [...pcmpAccountsQueryKeys.all(), 'counts', filters] as const,
  batches: (params: PcmpAccountsBatchesParams) =>
    [...pcmpAccountsQueryKeys.all(), 'batches', params] as const,
  batch: (batchNo: number, params: PcmpAccountsBatchProposalsParams) =>
    [...pcmpAccountsQueryKeys.all(), 'batch', batchNo, params] as const,
  redisburseBatches: (params: PcmpRedisburseBatchesParams) =>
    [...pcmpAccountsQueryKeys.all(), 'redisburse-batches', params] as const,
  redisburseBatch: (batchNo: number, params: PcmpRedisburseBatchProposalsParams) =>
    [...pcmpAccountsQueryKeys.all(), 'redisburse-batch', batchNo, params] as const,
  report: (batchNo: number) => [...pcmpAccountsQueryKeys.all(), 'report', batchNo] as const,
  redisburseReport: (batchNo: number, view: PcmpAccountsBatchView) =>
    [...pcmpAccountsQueryKeys.all(), 'redisburse-report', batchNo, view] as const,
};

export const pcmpAccountsQueueQueryOptions = (params: PcmpAccountsQueueParams) =>
  queryOptions({
    queryKey: pcmpAccountsQueryKeys.queue(params),
    queryFn: async (): Promise<PaginatedPcmpAccountsQueue> => {
      const res = await apiClient.get(API.pcmpAccounts.queue, { params });
      return paginatedPcmpAccountsQueueSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

export const pcmpAccountsCountsQueryOptions = (filters: PcmpQueueFilters) =>
  queryOptions({
    queryKey: pcmpAccountsQueryKeys.counts(filters),
    queryFn: async (): Promise<PcmpAccountsQueueCounts> => {
      const res = await apiClient.get(API.pcmpAccounts.queueCounts, { params: filters });
      return pcmpAccountsQueueCountsSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

export const pcmpAccountsBatchesQueryOptions = (params: PcmpAccountsBatchesParams) =>
  queryOptions({
    queryKey: pcmpAccountsQueryKeys.batches(params),
    queryFn: async (): Promise<PcmpAccountsBatches> => {
      const res = await apiClient.get(API.pcmpAccounts.batches, { params });
      return pcmpAccountsBatchesSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

// Fresh on every visit: other Accounts users disburse and fail its items.
export const pcmpAccountsBatchQueryOptions = (
  batchNo: number,
  params: PcmpAccountsBatchProposalsParams,
) =>
  queryOptions({
    queryKey: pcmpAccountsQueryKeys.batch(batchNo, params),
    queryFn: async (): Promise<PcmpAccountsBatchProposals> => {
      const res = await apiClient.get(API.pcmpAccounts.batchProposals(batchNo), { params });
      return pcmpAccountsBatchProposalsSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: 0,
  });

export const pcmpRedisburseBatchesQueryOptions = (params: PcmpRedisburseBatchesParams) =>
  queryOptions({
    queryKey: pcmpAccountsQueryKeys.redisburseBatches(params),
    queryFn: async (): Promise<PaginatedPcmpRedisburseBatches> => {
      const res = await apiClient.get(API.pcmpAccounts.redisburseBatches, { params });
      return paginatedPcmpRedisburseBatchesSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

export const pcmpRedisburseBatchQueryOptions = (
  batchNo: number,
  params: PcmpRedisburseBatchProposalsParams,
) =>
  queryOptions({
    queryKey: pcmpAccountsQueryKeys.redisburseBatch(batchNo, params),
    queryFn: async (): Promise<PcmpRedisburseBatchProposals> => {
      const res = await apiClient.get(API.pcmpAccounts.redisburseBatchProposals(batchNo), {
        params,
      });
      return pcmpRedisburseBatchProposalsSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: 0,
  });

export const pcmpAccountsBatchReportQueryOptions = (batchNo: number) =>
  queryOptions({
    queryKey: pcmpAccountsQueryKeys.report(batchNo),
    queryFn: async (): Promise<PcmpAccountsBatchReport> => {
      const res = await apiClient.get(API.pcmpAccounts.report(batchNo));
      return pcmpAccountsBatchReportSchema.parse(res.data);
    },
    staleTime: 0,
  });

/** The API names the file the same way; its header is not readable cross-origin. */
export function downloadPcmpAccountsBatchXlsx(batchNo: number, date: string): Promise<void> {
  return downloadApiFile(
    API.pcmpAccounts.exportXlsx(batchNo),
    `CMP_Accounts_Disbursement_Batch_${batchNo}_${date}.xlsx`,
  );
}

export async function disbursePcmpAccountsBatch(
  batchNo: number,
  payload: PcmpAccountsDisbursePayload,
): Promise<PcmpAccountsDisburseResult> {
  const res = await apiClient.post(API.pcmpAccounts.disburse(batchNo), payload);
  return pcmpAccountsDisburseResultSchema.parse(res.data);
}

/** Before payout only; a disbursed item is failed from its own row. */
export async function failPcmpAccountsBatch(
  batchNo: number,
  payload: PcmpAccountsBatchFailPayload,
): Promise<PcmpAccountsBatchFailResult> {
  const res = await apiClient.post(API.pcmpAccounts.disbursementFailed(batchNo), payload);
  return pcmpAccountsBatchFailResultSchema.parse(res.data);
}

export const pcmpRedisburseBatchReportQueryOptions = (
  batchNo: number,
  view: PcmpAccountsBatchView,
) =>
  queryOptions({
    queryKey: pcmpAccountsQueryKeys.redisburseReport(batchNo, view),
    queryFn: async (): Promise<PcmpRedisburseBatchReport> => {
      const res = await apiClient.get(API.pcmpAccounts.redisburseReport(batchNo), {
        params: { view },
      });
      return pcmpRedisburseBatchReportSchema.parse(res.data);
    },
    staleTime: 0,
  });

/** Named as the API names it; its header is not readable cross-origin. */
export function downloadPcmpRedisburseBatchXlsx(
  batchNo: number,
  view: PcmpAccountsBatchView,
  date: string,
): Promise<void> {
  return downloadApiFile(
    API.pcmpAccounts.redisburseExportXlsx(batchNo),
    `CMP_Accounts_Redisbursement_Batch_${batchNo}_${view}_${date}.xlsx`,
    { view },
  );
}

/** Gathers corrected proposals from the Reverified tab into a new batch (9000001 up). */
export async function createPcmpRedisburseBatch(
  payload: PcmpRedisburseCreatePayload,
): Promise<PcmpRedisburseCreateResult> {
  const res = await apiClient.post(API.pcmpAccounts.redisburseBatches, payload);
  return pcmpRedisburseCreateResultSchema.parse(res.data);
}

export async function disbursePcmpRedisburseBatch(
  batchNo: number,
  payload: PcmpAccountsDisbursePayload,
): Promise<PcmpAccountsDisburseResult> {
  const res = await apiClient.post(API.pcmpAccounts.redisburseDisburse(batchNo), payload);
  return pcmpAccountsDisburseResultSchema.parse(res.data);
}
